/**
 * ModelStatsPanel - Collapsible panel showing all 6 attributes with bars and descriptions.
 * Expanded by default, user can toggle to collapse.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { LLMModelOption } from '@/types';
import {
  getModelAttributes,
  ATTRIBUTE_FIXED_COLORS,
  ATTRIBUTE_FIXED_BAR_COLORS,
  formatCostDisplay,
  getAttributeDisplay,
} from '@/constants/llm/modelAttributes';

export function ModelStatsPanel({ model }: { model: LLMModelOption }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const attrs = getModelAttributes(model);

  if (!attrs) return null;

  const allStats: { abbrev: string; score: number | null; desc: string }[] = [
    {
      abbrev: 'SPD',
      score: attrs.speed,
      desc: t(
        'llm.attrs.speed_desc',
        'Based on latency (time to first token). Lower latency = higher speed.'
      ),
    },
    {
      abbrev: 'AGI',
      score: attrs.agility,
      desc: t(
        'llm.attrs.agility_desc',
        'Based on throughput (tokens per second). Higher TPS = higher agility.'
      ),
    },
    {
      abbrev: 'INT',
      score: attrs.intelligence,
      desc: t(
        'llm.attrs.intelligence_desc',
        'Based on context length. Larger context window = higher intelligence.'
      ),
    },
    {
      abbrev: 'PWR',
      score: attrs.power,
      desc: t(
        'llm.attrs.power_desc',
        'Based on max output tokens. Larger output capacity = higher power.'
      ),
    },
    {
      abbrev: 'ATK',
      score: attrs.attack,
      desc: `${formatCostDisplay(attrs.attack)} per 1M input tokens. ${t('llm.attrs.attack_desc', 'Higher price = stronger attack.')}`,
    },
    {
      abbrev: 'DEF',
      score: attrs.defense,
      desc: `${formatCostDisplay(attrs.defense)} per 1M output tokens. ${t('llm.attrs.defense_desc', 'Higher price = stronger defense.')}`,
    },
  ];

  return (
    <div className="border-t border-white/10 mt-2 pt-2">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="flex items-center gap-1.5 text-[10px] text-white/50 hover:text-white/70 transition-colors w-full"
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
        {t('llm.attrs.title', 'Model Attributes')}
      </button>
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {allStats.map((stat) => {
            const textColor = ATTRIBUTE_FIXED_COLORS[stat.abbrev] || 'text-white/50';
            const barColor = ATTRIBUTE_FIXED_BAR_COLORS[stat.abbrev] || 'bg-white/30';
            const { barPercent, label } = getAttributeDisplay(stat.abbrev, stat.score);
            return (
              <div key={stat.abbrev}>
                <div className="flex items-center gap-2">
                  <span className={`w-8 text-[10px] font-bold ${textColor}`}>{stat.abbrev}</span>
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${barColor} transition-all`}
                      style={{ width: `${barPercent}%` }}
                    />
                  </div>
                  <span className={`min-w-[24px] text-[10px] font-bold ${textColor} text-right`}>
                    {label}
                  </span>
                </div>
                <p className="text-[10px] text-white/40 ml-10 -mt-0.5">{stat.desc}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ModelStatsPanel;
